import React, { useState, useEffect, useRef } from "react"
import LoginForm from "./LoginForm"
import { UserProfile, ActiveView } from "../types/objects"
import { User, LogOut, LayoutDashboard, Menu, ShieldCheck, ChevronDown, BarChart3, ArrowRight, LogIn } from "lucide-react"
import { ADMIN_ROLE } from "../types/roles"

interface NavigationProps {
    user: UserProfile | null
    onLogin: (user: UserProfile, token: string) => void
    onLogout: () => void
    setView: (view: ActiveView) => void
    onToggleMenu: () => void
}

/** Public page anchors shown in the desktop navigation (order matters) */
export const PUBLIC_NAV_LINKS: { id: string; label: string }[] = [
    { id: "features", label: "Charakteristika" },
    { id: "about", label: "O nás" },
    { id: "services", label: "Služby" },
    { id: "products", label: "Produkty" },
    { id: "inquiry", label: "Poptávka" },
    { id: "pricing", label: "Ceník" },
    { id: "portfolio", label: "Galerie" },
    { id: "testimonials", label: "Reference" },
    { id: "team", label: "Tým" },
    { id: "contact", label: "Kontakt" }
]

/**
 * Navigation Component
 * Transparent over the hero, turns into a frosted bar after scrolling.
 * Highlights the section currently in the viewport (scroll spy).
 */
export const Navigation: React.FC<NavigationProps> = ({
    user,
    onLogin,
    onLogout,
    setView,
    onToggleMenu
}) => {
    const [showProfileMenu, setShowProfileMenu] = useState<boolean>(false)
    const [showLoginPopover, setShowLoginPopover] = useState<boolean>(false)
    const [showLogoutModal, setShowLogoutModal] = useState<boolean>(false)
    const [scrolled, setScrolled] = useState<boolean>(false)
    const [activeId, setActiveId] = useState<string>("")
    const isAdmin = user?.role?.name === ADMIN_ROLE

    const dropdownRef = useRef<HTMLLIElement>(null)
    const loginRef = useRef<HTMLLIElement>(null)

    /** Close profile dropdown / login popover when clicking outside */
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowProfileMenu(false)
            }
            if (loginRef.current && !loginRef.current.contains(event.target as Node)) {
                setShowLoginPopover(false)
            }
        }
        if (showProfileMenu || showLoginPopover) {
            document.addEventListener("mousedown", handleClickOutside)
        }
        return () => document.removeEventListener("mousedown", handleClickOutside)
    }, [showProfileMenu, showLoginPopover])

    /** Solid bar after the page is scrolled a bit */
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24)
        onScroll()
        window.addEventListener("scroll", onScroll, { passive: true })
        return () => window.removeEventListener("scroll", onScroll)
    }, [])

    /** Scroll spy over the public sections */
    useEffect(() => {
        if (user || typeof IntersectionObserver === "undefined") return

        const sections = PUBLIC_NAV_LINKS
            .map(link => document.getElementById(link.id))
            .filter((el): el is HTMLElement => !!el)

        if (sections.length === 0) return

        const io = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) setActiveId(entry.target.id)
                })
            },
            { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
        )
        sections.forEach(el => io.observe(el))
        return () => io.disconnect()
    }, [user])

    const getDisplayName = (name: string) => (name ? name.split("@")[0] : "")

    const isSolid = scrolled || !!user

    return (
        <nav className={`lp-nav${isSolid ? " lp-nav--solid" : ""}`}>
            <div className="lp-nav__inner">

                {/* LOGO SECTION */}
                <a
                    className="lp-nav__brand"
                    href="#header"
                    onClick={(e) => {
                        if (user) {
                            e.preventDefault()
                            setView("portfolio")
                        }
                    }}
                    aria-label="B2B Insight – úvod"
                >
                    <img src="/img/b2bticon.png" alt="Logo B2B Insight" className="lp-nav__logo" />
                    <span className="lp-nav__name">B2B Insight</span>
                </a>

                {/* DESKTOP NAVIGATION */}
                <ul className="lp-nav__links">
                    {!user ? (
                        <>
                            {PUBLIC_NAV_LINKS.map(link => (
                                <li key={link.id}>
                                    <a
                                        href={`#${link.id}`}
                                        className={`lp-nav__link${activeId === link.id ? " is-active" : ""}`}
                                    >
                                        {link.label}
                                    </a>
                                </li>
                            ))}
                            <li>
                                <a href="#contact" className="lp-btn lp-btn--primary lp-nav__cta">
                                    Konzultace zdarma <ArrowRight size={16} />
                                </a>
                            </li>
                            <li className="lp-nav__login" ref={loginRef}>
                                <button
                                    type="button"
                                    className={`lp-nav__login-toggle${showLoginPopover ? " is-open" : ""}`}
                                    onClick={() => setShowLoginPopover(v => !v)}
                                    aria-expanded={showLoginPopover}
                                    aria-haspopup="dialog"
                                >
                                    <LogIn size={16} /> Přihlásit
                                </button>
                                {showLoginPopover && (
                                    <div className="lp-nav__popover" role="dialog" aria-label="Přihlášení do CRM">
                                        <div className="lp-nav__popover-title">Přihlášení do CRM</div>
                                        <LoginForm onLogin={onLogin} />
                                    </div>
                                )}
                            </li>
                        </>
                    ) : (
                        <>
                            <li>
                                <button type="button" onClick={() => setView("portfolio")} className="lp-nav__user-btn">
                                    <LayoutDashboard size={18} /> Dashboard
                                </button>
                            </li>

                            {/* Shortcut to the document screening */}
                            <li>
                                <button type="button" onClick={() => setView("scoring")} className="lp-nav__user-btn lp-nav__user-btn--accent">
                                    <BarChart3 size={18} /> Screening
                                </button>
                            </li>

                            <li ref={dropdownRef} style={{ position: "relative", marginLeft: 8 }}>
                                <div
                                    className="lp-nav__profile"
                                    onClick={() => setShowProfileMenu(!showProfileMenu)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setShowProfileMenu(!showProfileMenu) }}
                                >
                                    <div style={{ width: 30, height: 30, borderRadius: "50%", background: isAdmin ? "#fef3c7" : "#e0e7ff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                        {isAdmin ? <ShieldCheck size={16} color="#d97706" /> : <User size={16} color="#4338ca" />}
                                    </div>
                                    <span style={{ fontSize: 14, fontWeight: 800, color: "#1e293b" }}>
                                        {getDisplayName(user.username)}
                                    </span>
                                    <ChevronDown size={16} color="#64748b" style={{ transform: showProfileMenu ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
                                </div>

                                {showProfileMenu && (
                                    <div className="lp-nav__dropdown">
                                        <div style={{ padding: 12, borderBottom: "1px solid #f1f5f9", marginBottom: 4 }}>
                                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                                                {isAdmin ? <ShieldCheck size={14} color="#d97706" /> : <User size={14} color="#64748b" />}
                                                <span style={{ fontSize: 11, fontWeight: 800, color: isAdmin ? "#d97706" : "#64748b", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                                                    {isAdmin ? "Administrátor" : "Uživatel"}
                                                </span>
                                            </div>
                                            <p style={{ margin: 0, fontSize: 13, fontWeight: 800, color: "#0f172a", wordBreak: "break-all" }}>
                                                {user.email || user.username}
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => { setShowProfileMenu(false); setShowLogoutModal(true) }}
                                            style={{
                                                width: "100%",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 10,
                                                padding: "10px 12px",
                                                border: "none",
                                                background: "transparent",
                                                color: "#ef4444",
                                                fontSize: 14,
                                                fontWeight: 700,
                                                cursor: "pointer",
                                                borderRadius: 10,
                                                fontFamily: "inherit"
                                            }}
                                        >
                                            <LogOut size={18} /> Odhlásit se
                                        </button>
                                    </div>
                                )}
                            </li>
                        </>
                    )}
                </ul>

                {/* MOBILE HAMBURGER TOGGLE */}
                <button
                    type="button"
                    className="lp-nav__burger"
                    aria-label="Otevřít menu"
                    onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        onToggleMenu()
                    }}
                >
                    <Menu size={24} />
                </button>
            </div>

            {/* LOGOUT CONFIRMATION MODAL */}
            {showLogoutModal && (
                <div style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100vw",
                    height: "100vh",
                    background: "rgba(15, 23, 42, 0.6)",
                    backdropFilter: "blur(4px)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 3000
                }}>
                    <div style={{
                        background: "#ffffff",
                        width: "90%",
                        maxWidth: 400,
                        borderRadius: 24,
                        padding: 32,
                        textAlign: "center",
                        animation: "lp-pop 0.25s cubic-bezier(0.2, 0.7, 0.2, 1)"
                    }}>
                        <div style={{ width: 64, height: 64, background: "#fef2f2", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
                            <LogOut size={32} color="#ef4444" />
                        </div>
                        <h3 style={{ fontSize: 22, fontWeight: 900, color: "#0f172a", marginBottom: 12 }}>Odhlášení</h3>
                        <p style={{ fontSize: 16, color: "#64748b", marginBottom: 32 }}>Opravdu se chcete odhlásit ze systému?</p>

                        <div style={{ display: "flex", gap: 12 }}>
                            <button
                                type="button"
                                onClick={() => setShowLogoutModal(false)}
                                style={{ flex: 1, padding: 14, borderRadius: 14, border: "1px solid #e2e8f0", background: "white", fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}
                            >
                                Zrušit
                            </button>
                            <button
                                type="button"
                                onClick={() => { setShowLogoutModal(false); onLogout() }}
                                style={{ flex: 1, padding: 14, borderRadius: 14, border: "none", background: "#ef4444", color: "white", fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}
                            >
                                Odhlásit
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </nav>
    )
}
